import {
    Box,
    Typography,
    useTheme,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
} from "@mui/material";
import { font } from "../../../../styles/font";
import { AI_Response_SentBy } from "../../../../constant/ApplicationVariables";
import ResponseMessageContainer from "./ResponseMessageContainer";
import UserMessageContainer from "./UserMessageContainer";
import { useState, useEffect, useCallback, useMemo } from "react";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import {
    ReplayOutlined,
    AutoAwesome,
    Assignment,
    Science,
    IntegrationInstructions,
    Timeline,
    CheckCircle,
    Build,
    Dashboard,
    CloudDone,
    TrendingUp,
} from "@mui/icons-material";

// ============================================================================
// extractMessageText — build a plain-text version of any message item.
// ============================================================================
const extractMessageText = (item) => {
    if (!item) return "";

    if (typeof item === "string") return item;
    if (item.chatMessageStr && typeof item.chatMessageStr === "string")
        return item.chatMessageStr;
    if (item.prompt && typeof item.prompt === "string") return item.prompt;
    if (item.message && typeof item.message === "string") return item.message;

    if (Array.isArray(item.list) && item.list.length > 0) {
        return item.list
            .map((entity) => {
                if (!entity || typeof entity !== "object")
                    return String(entity || "");
                const parts = [];
                if (entity.title || entity.name)
                    parts.push(`# ${entity.title || entity.name}`);
                if (entity.description)
                    parts.push(`Description: ${entity.description}`);
                if (entity.storyPoints !== undefined)
                    parts.push(`Story Points: ${entity.storyPoints}`);

                const renderList = (label, arr) => {
                    if (!Array.isArray(arr) || arr.length === 0) return;
                    parts.push(
                        `${label}:\n` +
                            arr
                                .map(
                                    (x) =>
                                        `- ${
                                            typeof x === "string"
                                                ? x
                                                : x?.text || JSON.stringify(x)
                                        }`
                                )
                                .join("\n")
                    );
                };

                renderList("Acceptance Criteria", entity.acceptanceCriteria);
                renderList("Preconditions", entity.preconditions);
                renderList("Expected Results", entity.expectedResults);

                if (Array.isArray(entity.steps) && entity.steps.length) {
                    parts.push(
                        "Steps:\n" +
                            entity.steps
                                .map(
                                    (s, i) =>
                                        `${i + 1}. ${
                                            typeof s === "string"
                                                ? s
                                                : s?.text || JSON.stringify(s)
                                        }`
                                )
                                .join("\n")
                    );
                }
                if (
                    Array.isArray(entity.testSteps) &&
                    entity.testSteps.length
                ) {
                    parts.push(
                        "Test Steps:\n" +
                            entity.testSteps
                                .map(
                                    (s, i) =>
                                        `${i + 1}. ${
                                            typeof s === "string"
                                                ? s
                                                : s?.text || JSON.stringify(s)
                                        }`
                                )
                                .join("\n")
                    );
                }
                return parts.join("\n");
            })
            .join("\n\n---\n\n");
    }

    if (typeof item._rawResponse === "string") return item._rawResponse;

    try {
        return JSON.stringify(item, null, 2);
    } catch {
        return "";
    }
};

// ============================================================================
// InlineCopyButton — one small icon button, ChatGPT-style.
// ============================================================================
function InlineCopyButton({ item, label }) {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        const text = extractMessageText(item);
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            } catch {
                return;
            }
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip
            title={copied ? "Copied!" : label || "Copy"}
            placement="top"
            arrow
        >
            <IconButton
                size="small"
                onClick={handleCopy}
                aria-label="copy message"
                sx={{
                    color: copied
                        ? theme.palette.success.main
                        : theme.palette.text.secondary,
                    padding: "4px",
                    borderRadius: "6px",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        color: copied
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.05)",
                    },
                }}
            >
                {copied ? (
                    <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
            </IconButton>
        </Tooltip>
    );
}

// ============================================================================
// MessagesMainContainer
// ============================================================================
export default function MessagesMainContainer({
    responseList,
    isContentGenerationLoading,
    onRetry,
}) {
    const theme = useTheme();
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const tips = [
        { icon: <AutoAwesome sx={{ fontSize: 10 }} />, title: "Generate User Stories", description: "Write clear user stories with acceptance criteria in seconds" },
        { icon: <Assignment sx={{ fontSize: 10 }} />, title: "Create Use Cases", description: "Define system behavior with detailed step-by-step scenarios" },
        { icon: <Science sx={{ fontSize: 10 }} />, title: "Build Test Cases", description: "Generate comprehensive test scenarios for quality assurance" },
        { icon: <IntegrationInstructions sx={{ fontSize: 10 }} />, title: "Automation Workflows", description: "Design automated test scripts and CI/CD pipelines" },
        { icon: <Timeline sx={{ fontSize: 10 }} />, title: "Project Structure", description: "Auto-generate folder structures and project templates" },
        { icon: <CheckCircle sx={{ fontSize: 10 }} />, title: "Quality Assurance", description: "Validate requirements and ensure complete coverage" },
        { icon: <Build sx={{ fontSize: 10 }} />, title: "Best Practices", description: "Follow industry standards for documentation and testing" },
        { icon: <Dashboard sx={{ fontSize: 10 }} />, title: "Agile Development", description: "Support sprint planning with ready-to-use artifacts" },
        { icon: <CloudDone sx={{ fontSize: 10 }} />, title: "Seamless Integration", description: "Connect with Jira, GitHub, and other dev tools" },
        { icon: <TrendingUp sx={{ fontSize: 10 }} />, title: "Team Productivity", description: "Reduce manual work and accelerate development cycles" },
    ];

    useEffect(() => {
        if (isContentGenerationLoading) {
            const interval = setInterval(() => {
                setCurrentTipIndex((prev) => (prev + 1) % tips.length);
            }, 4000);
            return () => clearInterval(interval);
        } else {
            setCurrentTipIndex(0);
        }
    }, [isContentGenerationLoading, tips.length]);

    // ========================================================================
    // Retry — always re-fires the same last user prompt.
    // ========================================================================
    const lastUserPrompt = useMemo(() => {
        const lastUserMessage = responseList
            ?.filter((item) => item?.sentBy === AI_Response_SentBy.user)
            .pop();
        return (
            lastUserMessage?.prompt || lastUserMessage?.chatMessageStr || ""
        );
    }, [responseList]);

    const handleRetry = useCallback(
        (overridePrompt) => {
            const prompt = overridePrompt || lastUserPrompt;

            if (!prompt) {
                console.warn(
                    "[MessagesMainContainer] Retry aborted: no prompt available"
                );
                setSnackbar({
                    open: true,
                    message: "Nothing to retry",
                    severity: "info",
                });
                return;
            }

            console.log(
                "[MessagesMainContainer] Retry fired with prompt:",
                prompt
            );

            if (typeof onRetry === "function") {
                onRetry(prompt);
            } else {
                console.warn(
                    "[MessagesMainContainer] No onRetry prop provided — dispatching retryGeneration event"
                );
                window.dispatchEvent(
                    new CustomEvent("retryGeneration", {
                        detail: { prompt },
                    })
                );
            }
        },
        [lastUserPrompt, onRetry]
    );

    // ========================================================================
    // Copy entire conversation
    // ========================================================================
    const handleCopyConversation = useCallback(async () => {
        if (!responseList || responseList.length === 0) {
            setSnackbar({
                open: true,
                message: "Nothing to copy yet",
                severity: "info",
            });
            return;
        }

        const lines = [];
        for (const item of responseList) {
            const isError = item?.isError === true || item?.error;
            if (isError) {
                lines.push(
                    `### Error\n${item?.error || item?.message || ""}`
                );
                continue;
            }
            const isUser = item?.sentBy === AI_Response_SentBy.user;
            const text = extractMessageText(item);
            if (!text) continue;
            lines.push(`### ${isUser ? "You" : "Assistant"}\n${text}`);
        }

        const fullText = lines.join("\n\n").trim();
        if (!fullText) {
            setSnackbar({
                open: true,
                message: "Nothing to copy",
                severity: "info",
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(fullText);
            setSnackbar({
                open: true,
                message: "Conversation copied",
                severity: "success",
            });
        } catch {
            setSnackbar({
                open: true,
                message: "Failed to copy",
                severity: "error",
            });
        }
    }, [responseList]);

    const hasMessages = responseList?.length > 0;

    return (
        <Box className="w-full h-full flex flex-col items-start gap-2 py-1">
            {/* Top-right: copy the entire conversation */}
            {hasMessages && (
                <Box className="w-full flex justify-end px-1">
                    <Tooltip
                        title="Copy entire conversation"
                        placement="top"
                        arrow
                    >
                        <IconButton
                            size="small"
                            onClick={handleCopyConversation}
                            aria-label="copy conversation"
                            sx={{
                                color: theme.palette.text.secondary,
                                padding: "4px",
                                borderRadius: "6px",
                                "&:hover": {
                                    color: theme.palette.primary.main,
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.08)"
                                            : "rgba(0,0,0,0.05)",
                                },
                            }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Messages */}
            {responseList?.map((item, index) => {
                const isUser = item?.sentBy === AI_Response_SentBy.user;
                const isAI =
                    item?.sentBy === AI_Response_SentBy.assistant ||
                    item?.sentBy === "assistant";
                const isError = item?.isError === true || item?.error;

                if (isError) {
                    return (
                        <ErrorBanner
                            key={item?.id || index}
                            error={
                                item?.error ||
                                item?.message ||
                                "Something went wrong"
                            }
                            onRetry={() => handleRetry()}
                            isLast={index === responseList.length - 1}
                        />
                    );
                }

                if (isUser) {
                    return (
                        <Box
                            key={item?.responseId || item?.id || index}
                            className="w-full flex justify-end"
                        >
                            <UserMessageContainer data={item} />
                        </Box>
                    );
                }

                // AI or unknown → bubble + copy button on the same row
                return (
                    <Box
                        key={item?.responseId || item?.id || index}
                        className="w-full flex justify-start"
                        sx={{ position: "relative" }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <ResponseMessageContainer
                                data={item}
                                isLastConversation={
                                    index === responseList.length - 1
                                }
                                onRetry={handleRetry}
                            />
                        </Box>

                        <Box
                            sx={{
                                alignSelf: "flex-start",
                                ml: 0.5,
                                mt: 0.5,
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                            }}
                        >
                            <InlineCopyButton
                                item={item}
                                label={isAI ? "Copy response" : "Copy"}
                            />
                        </Box>
                    </Box>
                );
            })}

            {/* Loading indicator */}
            {isContentGenerationLoading && (
                <Box className="w-full flex flex-col items-start gap-1.5 px-2 animate-fadeIn">
                    <Box
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg w-full max-w-xs"
                        sx={{
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? theme.palette.grey[800]
                                    : theme.palette.grey[50],
                            border: `1px solid ${theme.palette.divider}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                                borderColor: theme.palette.primary.main,
                                boxShadow: `0 2px 6px ${theme.palette.primary.main}15`,
                            },
                        }}
                    >
                        <Box className="flex items-center gap-1">
                            {[0, 0.2, 0.4].map((delay, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 5,
                                        height: 5,
                                        borderRadius: "50%",
                                        backgroundColor:
                                            theme.palette.primary.main,
                                        animation:
                                            "bounce 1.4s ease-in-out infinite",
                                        animationDelay: `${delay}s`,
                                        "@keyframes bounce": {
                                            "0%, 60%, 100%": {
                                                transform:
                                                    "translateY(0)",
                                                opacity: 0.3,
                                            },
                                            "30%": {
                                                transform:
                                                    "translateY(-4px)",
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                letterSpacing: "0.3px",
                            }}
                        >
                            Thinking...
                        </Typography>
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() =>
                    setSnackbar((s) => ({ ...s, open: false }))
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((s) => ({ ...s, open: false }))
                    }
                    sx={{ fontSize: "0.75rem" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

// ============================================================================
// ErrorBanner
// ============================================================================
function ErrorBanner({ error, onRetry, isLast }) {
    const theme = useTheme();
    const [showDetails, setShowDetails] = useState(false);

    const errorText = typeof error === "string" ? error : String(error || "");
    const lower = errorText.toLowerCase();

    const isCredentialError =
        lower.includes("credential") ||
        lower.includes("api key") ||
        lower.includes("configure") ||
        lower.includes("provider rejected");

    const isServerError =
        lower.includes("technical difficulties") ||
        lower.includes("server error") ||
        lower.includes("internal server");

    const handleRetry = () => {
        if (onRetry && typeof onRetry === "function") {
            onRetry();
        }
    };

    return (
        <Box className="w-full px-2 animate-fadeIn">
            <Box
                sx={{
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.error.main}`,
                    backgroundColor:
                        theme.palette.mode === "dark"
                            ? `${theme.palette.error.dark}15`
                            : `${theme.palette.error.light}15`,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        boxShadow: `0 4px 12px ${theme.palette.error.main}25`,
                    },
                    "@media (max-width: 600px)": {
                        borderRadius: "8px",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.error.main}30`,
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? `${theme.palette.error.dark}25`
                                : `${theme.palette.error.light}20`,
                        "@media (max-width: 600px)": {
                            flexWrap: "wrap",
                            gap: 1,
                            p: 1.5,
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.error.contrastText,
                            flexShrink: 0,
                            "@media (max-width: 600px)": {
                                width: 24,
                                height: 24,
                            },
                        }}
                    >
                        <ReportProblemIcon fontSize="small" />
                    </Box>

                    <Box className="flex-1">
                        <Typography
                            variant="body1"
                            fontWeight={600}
                            sx={{
                                color: theme.palette.error.main,
                                fontSize: "0.95rem",
                                "@media (max-width: 600px)": {
                                    fontSize: "0.8rem",
                                },
                            }}
                        >
                            {isCredentialError
                                ? "🔑 Authentication Error"
                                : isServerError
                                ? "Server Error"
                                : "Something Went Wrong"}
                        </Typography>
                    </Box>

                    <Button
                        size="small"
                        onClick={handleRetry}
                        startIcon={<ReplayOutlined />}
                        sx={{
                            color: theme.palette.primary.main,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: `${theme.palette.primary.main}15`,
                            },
                            "@media (max-width: 600px)": {
                                fontSize: "0.7rem",
                                padding: "4px 8px",
                            },
                        }}
                    >
                        Retry
                    </Button>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        "@media (max-width: 600px)": { p: 1.5 },
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.secondary,
                            mb: 1,
                            "@media (max-width: 600px)": {
                                fontSize: "0.8rem",
                            },
                        }}
                    >
                        {isCredentialError
                            ? "The AI provider rejected the API credentials. Please check your configuration."
                            : isServerError
                            ? "We are currently experiencing technical difficulties. Please try again later."
                            : errorText ||
                              "An unexpected error occurred. Please try again."}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            mt: 1,
                        }}
                    >
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setShowDetails(!showDetails)}
                            sx={{
                                color: theme.palette.text.secondary,
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.primary.main,
                                },
                                "@media (max-width: 600px)": {
                                    fontSize: "0.65rem",
                                },
                            }}
                        >
                            {showDetails
                                ? "Hide Details"
                                : "Show Details"}
                        </Button>

                        {isCredentialError && (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                    window.dispatchEvent(
                                        new CustomEvent(
                                            "openModelConfig"
                                        )
                                    );
                                }}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    backgroundColor:
                                        theme.palette.primary.main,
                                    "&:hover": {
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                    },
                                    "@media (max-width: 600px)": {
                                        fontSize: "0.65rem",
                                        padding: "4px 8px",
                                    },
                                }}
                            >
                                Configure API Key
                            </Button>
                        )}
                    </Box>

                    {showDetails && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                borderRadius: "6px",
                                backgroundColor:
                                    theme.palette.mode === "dark"
                                        ? `${theme.palette.grey[800]}50`
                                        : `${theme.palette.grey[100]}50`,
                                border: `1px solid ${theme.palette.divider}`,
                                maxHeight: "150px",
                                overflow: "auto",
                                "@media (max-width: 600px)": {
                                    p: 1,
                                    maxHeight: "100px",
                                },
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontFamily: "monospace",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    "@media (max-width: 600px)": {
                                        fontSize: "0.6rem",
                                    },
                                }}
                            >
                                {errorText ||
                                    "No additional details available"}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {!isLast && (
                    <Box
                        sx={{
                            p: 1.5,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: "flex",
                            justifyContent: "flex-end",
                            "@media (max-width: 600px)": {
                                p: 1,
                            },
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.text.disabled,
                                fontSize: "0.65rem",
                                "@media (max-width: 600px)": {
                                    fontSize: "0.55rem",
                                },
                            }}
                        >
                            This error occurred in a previous message
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export { InlineCopyButton, extractMessageText };